import { useEffect, useMemo, useRef, useState } from "react";
import type { Bank, RenderScanabilityReport, RenderWarning, VietQRData } from "../api";
import { renderQr, ApiError } from "../api";
import { formatAccountNumberDisplay, formatAmount } from "../format";
import { downloadSvg, svgToPngBlob } from "../download";
import { detectInAppBrowser, downloadImageWithFallback } from "../env";
import { markDesignCompleted } from "../completedDesign";
import FullscreenImageGuide from "./FullscreenImageGuide";
import DownloadFrames from "./DownloadFrames";
import WatermarkToggle from "./WatermarkToggle";
import ShareActions from "./ShareActions";
import ScanabilityBadge from "./ScanabilityBadge";

type TemplateInfo = { templateId: string; version: number; name: string; aspectRatio: string };

type Props = {
  bank: Bank;
  accountNumber: string;
  amount: number | undefined;
  qrData: VietQRData;
  svg: string;
  payload: string;
  template: TemplateInfo | null;
  warnings: RenderWarning[];
  /** Kết quả kiểm tra khả năng quét thật từ server. */
  scanability: RenderScanabilityReport | null;
  onReset: () => void;
  onChangeTemplate: () => void;
  onDesignCompleted?: () => void;
};

const VARIABLE_FIELDS: { key: string; label: string; placeholder: string }[] = [
  { key: "headline", label: "Tiêu đề", placeholder: "Ví dụ: Quét mã ủng hộ mình nhé" },
  { key: "subtitle", label: "Phụ đề", placeholder: "Ví dụ: Cảm ơn bạn rất nhiều" },
  { key: "merchant_name", label: "Tên quán / cửa hàng", placeholder: "Ví dụ: Cà phê Hạnh" },
];

export default function ResultStep({
  bank,
  accountNumber,
  amount,
  qrData,
  svg: initialSvg,
  payload: initialPayload,
  template,
  warnings: initialWarnings,
  scanability: initialScanability,
  onReset,
  onChangeTemplate,
  onDesignCompleted,
}: Props) {
  const [payloadOpen, setPayloadOpen] = useState(false);
  const [downloadMsg, setDownloadMsg] = useState<string | null>(null);
  const [pngBusy, setPngBusy] = useState(false);
  const [svgBusy, setSvgBusy] = useState(false);
  const [fullscreenGuide, setFullscreenGuide] = useState<string | null>(null);
  const [watermarkEnabled, setWatermarkEnabled] = useState(true);

  const inAppInfo = useMemo(() => detectInAppBrowser(), []);

  const [svg, setSvg] = useState(initialSvg);
  const [payload, setPayload] = useState(initialPayload);
  const [warnings, setWarnings] = useState(initialWarnings);
  const [scanability, setScanability] = useState(initialScanability);
  const [variables, setVariables] = useState<Record<string, string>>({});
  const [updating, setUpdating] = useState(false);
  const [updateError, setUpdateError] = useState<string | null>(null);

  const requestId = useRef(0);
  const skipNextRender = useRef(true);

  useEffect(() => {
    if (!template) return;
    if (skipNextRender.current) {
      // Bỏ qua lần chạy đầu — đã có ảnh render sẵn từ bước chọn mẫu, không cần gọi lại.
      skipNextRender.current = false;
      return;
    }

    const id = ++requestId.current;
    setUpdating(true);
    setUpdateError(null);

    const timer = setTimeout(() => {
      const vars: Record<string, string> = {};
      for (const [k, v] of Object.entries(variables)) {
        if (v.trim().length > 0) vars[k] = v;
      }
      renderQr({ templateId: template.templateId, templateVersion: template.version, qrData, variables: vars })
        .then((res) => {
          if (id !== requestId.current) return;
          setSvg(res.svg);
          setPayload(res.payload);
          setWarnings(res.warnings);
          // Kết quả server LUÔN ghi đè — không có heuristic client nào ở màn này để giữ lại
          // trong lúc chờ (I5, docs/01-architecture.md §1).
          setScanability(res.scanability);
        })
        .catch((err: unknown) => {
          if (id !== requestId.current) return;
          setUpdateError(
            err instanceof ApiError
              ? "Chưa cập nhật được chữ, mạng đang chập chờn. Thử lại giúp mình nhé."
              : "Có lỗi xảy ra, chưa cập nhật được chữ. Thử lại giúp mình nhé.",
          );
        })
        .finally(() => {
          if (id !== requestId.current) return;
          setUpdating(false);
        });
    }, 400);

    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [variables]);

  const svgDataUrl = useMemo(() => `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`, [svg]);
  const fileBase = `qr-${bank.shortName || bank.code}-${accountNumber}`.replace(/\s+/g, "");

  function notifyDesignCompleted() {
    markDesignCompleted();
    onDesignCompleted?.();
  }

  // Chuỗi fallback tải ảnh theo docs/73-facebook-integration.md §2.3: share file -> tải trực
  // tiếp (ngoài in-app browser) -> mở ảnh toàn màn hình + hướng dẫn chạm giữ để lưu. Không bao
  // giờ coi "đã gọi <a download>" là thành công mặc định trong WebView (docs/71-consumer-ux.md
  // §10.2) — luôn hiện thông báo tường minh theo đúng method thực sự dùng được.
  function handleDownloadSvg() {
    setSvgBusy(true);
    setDownloadMsg(null);
    const blob = new Blob([svg], { type: "image/svg+xml" });
    downloadImageWithFallback(blob, `${fileBase}.svg`, inAppInfo)
      .then((res) => {
        if (res.method === "share") setDownloadMsg("Đã mở bảng chia sẻ — chọn \"Lưu vào Ảnh\" để lưu nhé.");
        else if (res.method === "direct-download") setDownloadMsg("Đã lưu ảnh vào máy.");
        else setFullscreenGuide(res.imageUrl);
        notifyDesignCompleted();
      })
      .catch(() => {
        try {
          downloadSvg(svg, `${fileBase}.svg`);
          setDownloadMsg("Đã lưu ảnh vào máy.");
          notifyDesignCompleted();
        } catch {
          setDownloadMsg("Không tải được tự động. Hãy chạm giữ vào mã để lưu ảnh, hoặc mở trang này bằng Chrome/Safari.");
        }
      })
      .finally(() => setSvgBusy(false));
  }

  function handleDownloadPng() {
    setPngBusy(true);
    setDownloadMsg(null);
    svgToPngBlob(svg)
      .then((blob) =>
        downloadImageWithFallback(blob, `${fileBase}.png`, inAppInfo).then((res) => {
          if (res.method === "share") setDownloadMsg("Đã mở bảng chia sẻ — chọn \"Lưu vào Ảnh\" để lưu nhé.");
          else if (res.method === "direct-download") setDownloadMsg("Đã lưu ảnh PNG vào máy.");
          else setFullscreenGuide(res.imageUrl);
          notifyDesignCompleted();
        }),
      )
      .catch(() => {
        setDownloadMsg("Trình duyệt này chặn tải ảnh tự động. Hãy chạm giữ vào ảnh để lưu nhé.");
      })
      .finally(() => setPngBusy(false));
  }

  return (
    <section className="step result-step" aria-labelledby="step-result-title">
      <h1 id="step-result-title" className="step-title">
        {template ? `Mẫu "${template.name}" của bạn đã sẵn sàng` : "Mã QR của bạn đã sẵn sàng"}
      </h1>

      {inAppInfo.isInApp ? (
        <p className="hint-text small in-app-hint">
          Bạn đang mở trong ứng dụng {inAppInfo.app === "facebook" ? "Facebook" : inAppInfo.app === "instagram" ? "Instagram" : inAppInfo.app === "zalo" ? "Zalo" : "này"} —
          nếu tải ảnh không mượt, mình sẽ hiện ảnh to để bạn chạm giữ lưu lại nhé.
        </p>
      ) : null}

      <div className="qr-frame">
        {/* eslint-disable-next-line react/no-danger */}
        <div className="qr-svg-wrap" dangerouslySetInnerHTML={{ __html: svg }} />
        {updating ? <div className="qr-updating-badge">Đang cập nhật…</div> : null}
      </div>

      {/*
        Trạng thái quét lấy TỪ SCANABILITY THẬT của server (`scanability.label`/`bankingSafe`
        — `@qrstudio/scanability`), KHÔNG suy đoán từ `warnings.length` như trước (lỗi kiến
        trúc đã sửa: `warnings[]` là cảnh báo renderer, không phải kết quả kiểm scanability).
        `scanability === null` nghĩa là server chưa kiểm tra được — hiện trạng thái trung lập,
        KHÔNG BAO GIỜ hiện như đã "an toàn" (thà không nói gì còn hơn nói sai về an toàn
        thanh toán).
      */}
      {template ? (
        scanability ? (
          <ScanabilityBadge
            label={scanability.label}
            bankingSafe={scanability.bankingSafe}
            // Không truyền onRepair: chế độ Đơn giản chưa có endpoint áp repair patch tự động
            // cho luồng chọn mẫu (packages/scanability/src/repair.ts đã có hàm repair() thuần,
            // chỉ thiếu route HTTP dùng nó + UI áp patch). TODO(scanability-repair): nối route
            // + nút "Sửa giúp tôi" khi có — không dựng nút giả không hoạt động.
          />
        ) : (
          <p className="result-status result-status-checking" role="status">
            Chưa kiểm tra được độ an toàn quét cho mã này — thử tải lại trang nhé.
          </p>
        )
      ) : null}

      {template ? (
        <div className="variable-edit-group">
          <p className="hint-text small">Chạm vào ô để sửa chữ trên mẫu, mình cập nhật liền.</p>
          {VARIABLE_FIELDS.map((f) => (
            <div className="field" key={f.key}>
              <label htmlFor={`var-${f.key}`}>{f.label}</label>
              <input
                id={`var-${f.key}`}
                className="input"
                placeholder={f.placeholder}
                value={variables[f.key] ?? ""}
                onChange={(e) => setVariables((v) => ({ ...v, [f.key]: e.target.value }))}
              />
            </div>
          ))}
          {updateError ? <p className="field-hint field-hint-error">{updateError}</p> : null}
        </div>
      ) : null}

      <div className="qr-summary">
        <strong className="qr-summary-bank">{bank.shortName}</strong>
        <span className="qr-summary-account">{formatAccountNumberDisplay(accountNumber)}</span>
        {amount !== undefined ? <span className="qr-summary-amount">{formatAmount(amount)} ₫</span> : null}
      </div>

      <p className="scan-reminder">
        Hãy tự quét thử bằng app ngân hàng của bạn trước khi đưa cho người khác.
      </p>

      <button type="button" className="btn btn-secondary btn-block" onClick={onChangeTemplate}>
        {template ? "Đổi mẫu khác" : "Chọn mẫu cho đẹp hơn"}
      </button>

      <WatermarkToggle
        enabled={watermarkEnabled}
        onChange={setWatermarkEnabled}
        skipped={warnings.some((w) => w.code === "WATERMARK_SKIPPED")}
      />

      <div className="result-actions">
        <button type="button" className="btn btn-primary btn-block" onClick={handleDownloadSvg} disabled={svgBusy}>
          {svgBusy ? "Đang chuẩn bị ảnh…" : "Tải ảnh về"}
        </button>
        <button type="button" className="btn btn-secondary btn-block" onClick={handleDownloadPng} disabled={pngBusy}>
          {pngBusy ? "Đang tạo ảnh PNG…" : "Tải ảnh PNG"}
        </button>
        <a className="hidden-link-fallback" href={svgDataUrl} download hidden aria-hidden="true">
          tải xuống dự phòng
        </a>
        <p className="hint-text small">Nếu không tải được, hãy chạm giữ vào mã để lưu ảnh.</p>
        {downloadMsg ? <p className="hint-text small">{downloadMsg}</p> : null}

        {template ? <DownloadFrames currentAspectRatio={template.aspectRatio} /> : null}

        <ShareActions svg={svg} filename={`${fileBase}.png`} onShared={notifyDesignCompleted} />

        <button type="button" className="btn btn-secondary btn-block" onClick={onReset}>
          Tạo mã khác
        </button>

      </div>

      <div className="payload-debug">
        <button type="button" className="link-toggle" onClick={() => setPayloadOpen((v) => !v)}>
          {payloadOpen ? "Ẩn chuỗi dữ liệu QR ▲" : "Xem chuỗi dữ liệu QR ▼"}
        </button>
        {payloadOpen ? <pre className="payload-text">{payload}</pre> : null}
      </div>

      {fullscreenGuide ? (
        <FullscreenImageGuide
          imageUrl={fullscreenGuide}
          os={inAppInfo.os}
          onClose={() => {
            URL.revokeObjectURL(fullscreenGuide);
            setFullscreenGuide(null);
          }}
        />
      ) : null}
    </section>
  );
}
